import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Upload, FileText, Pencil, Ban, Printer, FileDown, Mail } from "lucide-react";
import { API, authHeaders, money, inputCls, labelCls, Modal, VoidDialog, uploadFinanceFile } from "./shared";

const EMPTY = { customer_name: "", customer_email: "", vehicle_id: "", rental_start: "", rental_end: "",
  date: new Date().toISOString().slice(0, 10), amount: "", payment_method: "Cash", revenue_type: "Offline Rental", notes: "" };

export default function ManualRevenueModule({ token, onAuthError, options, vehicles }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [voiding, setVoiding] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/finance/revenue`, authHeaders(token)).then((r) => setItems(r.data || []))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doVoid = async (reason) => {
    setBusy(true);
    try {
      await axios.post(`${API}/finance/revenue/${voiding.id}/void`, { reason }, authHeaders(token));
      toast.success(`${voiding.number} voided`);
      setVoiding(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not void"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await fetch(`${API}/finance/revenue/${r.id}/receipt.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${r.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const emailReceipt = async (r) => {
    const to = window.prompt("Email this receipt to:", r.customer_email || "");
    if (!to || !to.trim()) return;
    try {
      await axios.post(`${API}/finance/revenue/${r.id}/email-receipt`, { to: to.trim() }, authHeaders(token));
      toast.success(`Receipt ${r.number} emailed to ${to.trim()} (PDF attached)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
  };

  const esc = (s) => String(s ?? "—").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  const printReceipt = (r) => {
    const v = vehicles.find((x) => x.id === r.vehicle_id);
    const w = window.open("", "_blank", "width=720,height=860");
    if (!w) { toast.error("Allow pop-ups to print receipts."); return; }
    w.document.title = `Receipt ${esc(r.number)}`;
    w.document.body.innerHTML = `
<div style="font-family:Arial,sans-serif;max-width:640px;margin:30px auto;color:#111;">
<div style="border-bottom:3px solid #F2B705;padding-bottom:10px;margin-bottom:16px;">
<h2 style="margin:0;">AUTO-HIRE STL</h2><span style="color:#666;font-size:13px;">Manual Revenue Receipt · ${esc(r.number)}</span></div>
<table style="width:100%;font-size:14px;border-collapse:collapse;">
${[["Date Received", r.date], ["Customer", r.customer_name], ["Revenue Type", r.revenue_type],
   ["Vehicle", v ? `${v.year} ${v.make} ${v.model}` : "—"],
   ["Rental Period", r.rental_start ? `${r.rental_start} → ${r.rental_end || "—"}` : "—"],
   ["Payment Method", r.payment_method], ["Notes", r.notes || "—"], ["Entered By", r.entered_by]]
  .map(([k, val]) => `<tr><td style="padding:6px 0;color:#666;width:180px;">${esc(k)}</td><td style="padding:6px 0;">${esc(val || "—")}</td></tr>`).join("")}
</table>
<div style="margin-top:20px;padding:14px;background:#f7f7f7;border-radius:8px;font-size:20px;font-weight:bold;">
Amount Received: $${Number(r.amount || 0).toFixed(2)}</div>
<p style="color:#999;font-size:11px;margin-top:24px;">Manual Revenue Entry recorded outside the online booking flow. Thank you for your business!</p>
</div>`;
    setTimeout(() => w.print(), 300);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  const active = items.filter((i) => i.status !== "voided");
  const total = active.reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="manual-revenue-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-white font-bold">Manual Revenue Ledger</h3>
          <p className="text-white/45 text-sm">{active.length} entries · {money(total)} collected outside the online booking flow.</p>
        </div>
        <button data-testid="add-manual-revenue-btn" onClick={() => setEditing("new")}
          className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold hover:bg-white transition-colors">
          <Plus size={15} /> Add Manual Revenue
        </button>
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl overflow-hidden">
        {items.length === 0 && <p className="text-white/35 text-sm p-5">No manual revenue recorded yet.</p>}
        <div className="divide-y divide-white/5">
          {items.map((r) => (
            <div key={r.id} data-testid={`manual-revenue-row-${r.id}`} className={`px-4 py-3 ${r.status === "voided" ? "opacity-45" : ""}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-[#F2B705] font-semibold">{r.number}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F2B705]/15 text-[#F2B705] font-semibold">Manual Revenue Entry</span>
                <span className="text-white/85">{r.revenue_type}</span>
                {r.deposit_number && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">Deposit {r.deposit_number}</span>}
                {r.status === "voided" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">VOIDED</span>}
                <span className="ml-auto font-bold text-green-400">{money(r.amount)}</span>
              </div>
              <div className="text-white/45 text-xs mt-1">
                {r.date} · {r.customer_name}{r.customer_email ? ` (${r.customer_email})` : ""}
                {r.vehicle_name ? ` · ${r.vehicle_name}` : ""}{r.rental_start ? ` · ${r.rental_start} → ${r.rental_end || "—"}` : ""}
                {" · "}{r.payment_method} · entered by {r.entered_by}
                {r.last_modified_by ? ` · edited by ${r.last_modified_by}` : ""}
              </div>
              {r.notes && <div className="text-white/35 text-xs mt-0.5 italic">{r.notes}</div>}
              {r.status === "voided" && <div className="text-red-400/80 text-xs mt-0.5">Voided by {r.voided_by} — {r.void_reason}</div>}
              <div className="flex items-center gap-3 mt-1.5">
                {r.receipt_path && (
                  <a href={`${API}/files/${r.receipt_path}?auth=${token}`} target="_blank" rel="noreferrer"
                    className="text-[#F2B705] hover:underline flex items-center gap-1 text-xs"><FileText size={12} /> Receipt file</a>
                )}
                {r.status !== "voided" && <>
                  <button data-testid={`mr-edit-${r.id}`} onClick={() => setEditing(r)} className="flex items-center gap-1 text-xs text-white/50 hover:text-white"><Pencil size={12} /> Edit</button>
                  <button data-testid={`mr-print-${r.id}`} onClick={() => printReceipt(r)} className="flex items-center gap-1 text-xs text-white/50 hover:text-white"><Printer size={12} /> Print Receipt</button>
                  <button data-testid={`mr-pdf-${r.id}`} onClick={() => downloadPdf(r)} className="flex items-center gap-1 text-xs text-white/50 hover:text-white"><FileDown size={12} /> PDF</button>
                  <button data-testid={`mr-email-${r.id}`} onClick={() => emailReceipt(r)} className="flex items-center gap-1 text-xs text-white/50 hover:text-white"><Mail size={12} /> Email Receipt</button>
                  <button data-testid={`mr-void-${r.id}`} onClick={() => setVoiding(r)} className="flex items-center gap-1 text-xs text-white/50 hover:text-red-400"><Ban size={12} /> Void</button>
                </>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <RevenueForm token={token} options={options} vehicles={vehicles}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {voiding && <VoidDialog label={`Manual Revenue ${voiding.number}`} busy={busy} onConfirm={doVoid} onClose={() => setVoiding(null)} />}
    </div>
  );
}

function RevenueForm({ token, options, vehicles, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? {
    customer_name: initial.customer_name || "", customer_email: initial.customer_email || "",
    vehicle_id: initial.vehicle_id || "", rental_start: initial.rental_start || "", rental_end: initial.rental_end || "",
    date: initial.date || "", amount: String(initial.amount ?? ""), payment_method: initial.payment_method || "Cash",
    revenue_type: initial.revenue_type || "Offline Rental", notes: initial.notes || "",
  } : { ...EMPTY });
  const [receipt, setReceipt] = useState(initial?.receipt_path ? { path: initial.receipt_path, content_type: initial.receipt_content_type, filename: initial.receipt_filename } : null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { setReceipt(await uploadFinanceFile(token, file)); toast.success("Receipt attached"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error("Enter a customer / person name"); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter an amount greater than $0"); return; }
    setSaving(true);
    const payload = { ...form, amount: Number(form.amount),
      receipt_path: receipt?.path || null, receipt_content_type: receipt?.content_type || null, receipt_filename: receipt?.filename || null };
    try {
      if (initial) {
        await axios.patch(`${API}/finance/revenue/${initial.id}`, payload, authHeaders(token));
        toast.success("Entry updated");
      } else {
        await axios.post(`${API}/finance/revenue`, payload, authHeaders(token));
        toast.success("Manual revenue recorded");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={initial ? `Edit ${initial.number}` : "Add Manual Revenue"} onClose={onClose} wide>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="col-span-2 md:col-span-1"><label className={labelCls}>Customer / Person Name *</label>
          <input data-testid="mr-form-name" className={inputCls} value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></div>
        <div><label className={labelCls}>Link to Customer (email)</label>
          <input data-testid="mr-form-email" className={inputCls} placeholder="Optional" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></div>
        <div><label className={labelCls}>Related Vehicle</label>
          <select data-testid="mr-form-vehicle" className={inputCls} value={form.vehicle_id} onChange={(e) => set("vehicle_id", e.target.value)}>
            <option value="">No vehicle</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model}</option>)}
          </select></div>
        <div><label className={labelCls}>Rental Start</label>
          <input data-testid="mr-form-start" type="date" className={inputCls} value={form.rental_start} onChange={(e) => set("rental_start", e.target.value)} /></div>
        <div><label className={labelCls}>Rental End</label>
          <input data-testid="mr-form-end" type="date" className={inputCls} value={form.rental_end} onChange={(e) => set("rental_end", e.target.value)} /></div>
        <div><label className={labelCls}>Date Received</label>
          <input data-testid="mr-form-date" type="date" className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
        <div><label className={labelCls}>Amount Received *</label>
          <input data-testid="mr-form-amount" type="number" step="0.01" className={inputCls} value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
        <div><label className={labelCls}>Payment Method</label>
          <select data-testid="mr-form-method" className={inputCls} value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)}>
            {(options?.payment_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select></div>
        <div><label className={labelCls}>Revenue Type</label>
          <select data-testid="mr-form-type" className={inputCls} value={form.revenue_type} onChange={(e) => set("revenue_type", e.target.value)}>
            {(options?.revenue_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div className="col-span-2 md:col-span-3"><label className={labelCls}>Notes</label>
          <input data-testid="mr-form-notes" className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <label data-testid="mr-form-receipt" className="inline-flex items-center gap-2 cursor-pointer border border-white/15 hover:border-[#F2B705]/50 rounded-md px-3 py-2 text-sm text-white/60 transition-colors">
          {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />}
          {receipt ? "Receipt attached ✓" : "Upload receipt / screenshot"}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} />
        </label>
        <button data-testid="mr-form-save" onClick={save} disabled={saving}
          className="ml-auto px-5 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-60">
          {saving ? "Saving…" : initial ? "Save Changes" : "Record Revenue"}
        </button>
      </div>
    </Modal>
  );
}
